import {useEffect} from "react";
import {useForm, Controller} from "react-hook-form";
import {Checkbox, Alert} from "antd";
import {ModalForm} from "~/components";
import {NumberField} from "~/components/Forms";

/**
 * Modal cấu hình & bắt đầu một lần crawl. Cấu hình thuộc TỪNG lần crawl (không lưu ở
 * project), mặc định kế thừa lần crawl trước. Dùng ModalForm + hệ thống Form chuẩn.
 *
 * Độ trễ giữa các request KHÔNG cho người dùng chỉnh — hệ thống tự quyết (baseline + robots
 * Crawl-delay) để tránh dội request quá tải website. `limits` = giới hạn gói của chủ dự án
 * (BE trả về) để user biết trần của mình trước khi chạy.
 */
function RunCrawlForm({open, submitting, defaultValues, limits, onCancel, onSubmit}) {

	// Trần theo gói (fallback an toàn nếu BE chưa trả limits).
	const maxPagesCap = limits?.max_pages || 2000;
	const maxDepthCap = limits?.max_depth || 10;
	const waitMin = limits?.wait_min || 0;

	const {control, handleSubmit, reset} = useForm({
		defaultValues: {
			max_depth: 3,
			max_pages: Math.min(100, maxPagesCap),
			respect_robots: true,
		},
	});

	useEffect(() => {
		if (open) {
			reset({
				max_depth: 3,
				max_pages: Math.min(100, maxPagesCap),
				...defaultValues,
				// Trần luôn ép về giới hạn gói dù lần trước lưu lớn hơn (vd sau khi hạ gói).
				...(defaultValues?.max_pages ? {max_pages: Math.min(defaultValues.max_pages, maxPagesCap)} : {}),
				// Tôn trọng robots.txt LUÔN tick sẵn mỗi lần mở form (không kế thừa lựa chọn cũ).
				respect_robots: true,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	return (
		<ModalForm
			open={open}
			icon="fa-light fa-play"
			title="Chạy crawl"
			subtitle="Cấu hình phạm vi crawl cho lần chạy này"
			onCancel={onCancel}
			onOk={handleSubmit(onSubmit)}
			okText="Bắt đầu crawl"
			loading={submitting}
		>
			{limits && (
				<Alert
					type={waitMin > 0 ? "warning" : "info"}
					showIcon
					style={{marginBottom: 16}}
					message={`Giới hạn gói ${limits.plan_name}`}
					description={
						<ul style={{margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7}}>
							<li>Số trang tối đa mỗi lần crawl: <strong>{limits.max_pages.toLocaleString()}</strong></li>
							<li>
								Khoảng cách tối thiểu giữa 2 lần crawl:{' '}
								<strong>{limits.interval_min > 0 ? `${limits.interval_min} phút` : 'không giới hạn'}</strong>
							</li>
							{waitMin > 0 && (
								<li style={{color: '#d46b08'}}>
									Bạn vừa crawl gần đây — có thể chạy lại sau <strong>~{waitMin} phút</strong>.
								</li>
							)}
						</ul>
					}
				/>
			)}

			<Controller control={control} name="max_depth" render={({field}) => (
				<NumberField label="Độ sâu tối đa (click từ trang chủ)" min={0} max={maxDepthCap} {...field} />
			)} />
			<Controller control={control} name="max_pages" render={({field}) => (
				<NumberField label={`Số trang tối đa (tối đa ${maxPagesCap.toLocaleString()})`} min={1} max={maxPagesCap} {...field} />
			)} />
			<Controller control={control} name="respect_robots" render={({field}) => (
				<div className="form-group">
					<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)}>
						Tôn trọng robots.txt (Disallow + Crawl-delay)
					</Checkbox>
					<div style={{fontSize: 12, color: '#8c8c8c', marginTop: 4, marginLeft: 24}}>
						Nên bật để không crawl trang bị chặn. Độ trễ giữa các request do hệ thống tự điều chỉnh
						(theo Crawl-delay của robots.txt) nhằm tránh gây quá tải website.
					</div>
				</div>
			)} />
		</ModalForm>
	);
}

export default RunCrawlForm;
